import React, { useState } from "react";
import { Button, Grid, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomDialogBox from "../components/CustomDialogBox";
import TextInput from "../components/form/TextInput";
import { useForm } from "react-hook-form";
import { highlightField } from "../utils/Commons";
import API from "../config/axios";
import { toast } from "react-hot-toast";
import { useAuth } from "../hooks/auth";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";

const ProfileDialogBox = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [inputErrors, setInputErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const auth = useAuth();

  useEffect(() => {
    getProfile();
    // eslint-disable-next-line
  }, []);

  const validationSchema = yup.object().shape({
    name: yup.string().required(t("customers.name_required")),
    email: yup
      .string()
      .required(t("loginPage.emailrequired"))
      .email(t("loginPage.emailInvalid")),
  });

  const { handleSubmit, control, reset } = useForm({
    // defaultValues: defaultValues,
    mode: "onBlur",
    resolver: yupResolver(validationSchema),
  });

  const onError = (errors) => {
    try {
      highlightField(Object.keys(errors)[0]);
    } catch (error) {
      console.warn("Field cannot be highlighted");
    }
  };

  const getProfile = () => {
    setIsLoading(true);
    const requestStartTime = Date.now();
    API.get("users/profile")
      .then((res) => {
        const resData = res.data;

        const updateProfile = {
          name: resData.data.name,
          email: resData.data.email,
        };

        reset(updateProfile);
      })
      .catch(() => {
        toast.error("Something went wrong");
      })
      .finally(() => {
        const requestEndTime = Date.now();
        const timeOutMileSeconds = requestEndTime - requestStartTime;

        if (timeOutMileSeconds > 300) {
          setIsLoading(false);
        } else {
          setTimeout(() => {
            setIsLoading(false);
          }, 300);
        }
      });
  };

  const onSubmit = (data) => {
    delete data.email
    setIsLoading(true);
    const requestStartTime = Date.now();
    API.put("users/profile", data)
      .then((res) => {
        const resData = res.data;

        console.log("resData", resData);
        setInputErrors({});

        if (resData.status) {
          toast.success(t("profileUpdated"));
          onClose();
          auth.login(resData.data);
          //   auth.logout();
          //   navigate("/login");
        } else {
          if (resData && resData.type === "VALIDATION") {
            setInputErrors(resData.error);
            try {
              highlightField(Object.keys(resData.error)[0]);
            } catch (error) {
              console.warn("Field cannot be highlighted");
            }
          } else {
            toast.error(resData?.error?.message || "Something went wrong");
          }
        }
      })
      .catch(() => {
        toast.error("Something went wrong");
      })
      .finally(() => {
        const requestEndTime = Date.now();
        const timeOutMileSeconds = requestEndTime - requestStartTime;

        if (timeOutMileSeconds > 300) {
          setIsLoading(false);
        } else {
          setTimeout(() => {
            setIsLoading(false);
          }, 300);
        }
      });
  };

  return (
    <CustomDialogBox
      title={t("profile")}
      isOpen={isOpen}
      onClose={onClose}
      actions={
        <Stack
          mr={1}
          spacing={4}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={() => {
              onClose();
            }}
          >
            {t("common_btn.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
            onClick={handleSubmit(onSubmit)}
            loading={isLoading}
          >
            {t("common_btn.submit")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Grid container item spacing={3}>
          <Grid item xs={12} sm={12}>
            <TextInput
              name="name"
              type="text"
              label={t("customers.fields.name")}
              control={control}
              customError={inputErrors.name || null}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextInput
              disable
              name="email"
              type="text"
              label={t("customers.fields.email")}
              control={control}
              customError={inputErrors.email || null}
            />
          </Grid>
        </Grid>
      </form>
    </CustomDialogBox>
  );
};

export default ProfileDialogBox;
